import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/lib/errors';

import { useAuth } from '../auth-context';
import { AuthLayout } from '../components/auth-layout';
import { FormAlert } from '../components/form-alert';
import { AuthSplash } from '../components/full-page-state';
import { redirectTarget } from '../redirect';
import { loginSchema, type LoginValues } from '../schemas';

export function LoginPage() {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'loading') return <AuthSplash />;
  if (status === 'authenticated') return <Navigate to={redirectTarget(location.state)} replace />;

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    try {
      await signIn(email, password);
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  });

  return (
    <AuthLayout title="Iniciar sesión" description="Ingresa con la cuenta que te asignó SORAI.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <FormAlert>{formError}</FormAlert>}

        <FormField label="Correo" error={errors.email?.message} required>
          {(control) => (
            <Input
              {...control}
              {...register('email')}
              type="email"
              autoComplete="username"
              inputMode="email"
              placeholder="nombre@sorai.com.pe"
            />
          )}
        </FormField>

        <FormField label="Contraseña" error={errors.password?.message} required>
          {(control) => (
            <div className="relative">
              <Input
                {...control}
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => {
                  setShowPassword((value) => !value);
                }}
                className="absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-surface-sunken hover:text-foreground"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className="size-4" aria-hidden="true" />
                ) : (
                  <Eye className="size-4" aria-hidden="true" />
                )}
              </button>
            </div>
          )}
        </FormField>

        <div className="flex justify-end">
          <Link
            to="/recuperar-contrasena"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
          Ingresar
        </Button>

        <p className="pt-2 text-center text-xs text-muted-foreground">
          Las cuentas son creadas por un administrador. Si necesitas acceso, solicítalo al equipo de
          SORAI.
        </p>
      </form>
    </AuthLayout>
  );
}
